import {useCustomQuery, useCustomerId} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useMemo} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../utils/site-config'
import {useBrand, toBrandCode} from '../contexts/brand-context'
import {useEnrichVickyProducts} from './use-enrich-vicky-products'

/**
 * Unified hook to fetch VickyAI recommendations based on AIModel type.
 * Handles Vicky recommendation types: recommendations, similars, bought-together, shop-the-look, semantic-search, trending.
 *
 * Performance Strategy:
 * 1. Fetch VickyAI data first (has images, variants, descriptions - from backend cache).
 * 2. Reshape against the SFCC master product (via `useEnrichVickyProducts`) so
 *    the result matches a PLP search hit — full image groups for every color,
 *    full variants/variationAttributes/variationGroups, with a
 *    `representedProduct` reference pinning the recommended color.
 * 3. Merge fresh price / promotions / inventory pulled per-variationGroup so
 *    the tile shows the values the shopper will pay.
 * 4. Don't block initial render — show Vicky's payload immediately, enrich
 *    in the background as the SFCC fetches resolve.
 *
 * @param {string} aiModel - Type of recommendation ('recommendations', 'similars', 'bought-together', 'shop-the-look', 'semantic-search', 'trending')
 * @param {string} productSku - Product SKU for product-scoped models, or search query for semantic-search
 * @returns {Object} {products, isLoading, productIds}
 */
export const useVickyAIRecommendations = (aiModel, productSku = null) => {
    const {app: appConfig} = getConfig()
    const {site, locale} = useMultiSite()
    const siteId = site?.id
    const {brandId} = useBrand()
    const brandCode = toBrandCode(brandId)
    const customerId = useCustomerId()
    const isVickyAIEnabled = getSiteEnvVarValue(appConfig, 'VICKY_ENABLED', siteId) === 'true'
    const localeId = typeof locale === 'string' ? locale : locale?.id
    const countryCode = localeId?.split(/[-_]/).pop()?.toUpperCase() || 'CA'

    // Determine endpoint and parameters based on AIModel
    // Memoize to prevent creating new config object on every render
    const config = useMemo(() => {
        switch (aiModel?.toLowerCase()) {
            case 'skip':
                // Skip fetching - used when we don't have required data yet
                return {
                    endpointPath: '/recommendations',
                    parameters: {},
                    enabled: false
                }
            case 'similars':
            case 'similar':
                return {
                    endpointPath: '/similars',
                    parameters: {
                        siteId: site?.id,
                        c_sku: productSku,
                        c_brand: brandCode
                    },
                    enabled: isVickyAIEnabled && Boolean(productSku && site?.id)
                }
            case 'bought-together':
            case 'boughttogether':
            case 'frequently-bought-together':
                return {
                    endpointPath: '/bought-together',
                    parameters: {
                        siteId: site?.id,
                        c_sku: productSku,
                        c_brand: brandCode
                    },
                    enabled: isVickyAIEnabled && Boolean(productSku && site?.id)
                }
            case 'shop-the-look':
            case 'shopthelook':
            case 'shop-the-look-products':
                return {
                    endpointPath: '/shop-the-look',
                    parameters: {
                        siteId: site?.id,
                        c_sku: productSku,
                        c_brand: brandCode
                    },
                    enabled: isVickyAIEnabled && Boolean(productSku && site?.id)
                }
            case 'semantic-search':
            case 'semanticsearch':
                return {
                    endpointPath: '/semantic-search',
                    parameters: {
                        siteId: site?.id,
                        c_searchQuery: productSku,
                        c_brand: brandCode,
                        c_country: countryCode,
                        ...(localeId ? {locale: localeId} : {})
                    },
                    enabled: isVickyAIEnabled && Boolean(productSku && site?.id)
                }
            case 'trending':
            case 'trending-products':
                return {
                    endpointPath: '/trending',
                    parameters: {
                        siteId: site?.id,
                        c_brand: brandCode,
                        c_country: countryCode,
                        ...(localeId ? {locale: localeId} : {})
                    },
                    enabled: isVickyAIEnabled && Boolean(site?.id)
                }
            case 'recommendations':
            case 'recommended':
            default:
                return {
                    endpointPath: '/recommendations',
                    parameters: {
                        siteId: site?.id,
                        c_uid: customerId || 'anonymous',
                        c_brand: brandCode
                    },
                    enabled: isVickyAIEnabled && Boolean(site?.id)
                }
        }
    }, [aiModel, site?.id, productSku, customerId, brandCode, countryCode, localeId])

    const {data: recommendations, isLoading} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: config.parameters,
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: config.endpointPath,
                    apiName: 'vickyai'
                }
            },
            rawResponse: false
        },
        {
            // Add explicit query key to prevent cache conflicts between different SKU queries.
            // Each unique combination of (endpoint, SKU(s), siteId) gets its own cache entry.
            queryKey: ['vickyai', config.endpointPath, productSku, site?.id],
            enabled: config.enabled,
            staleTime: 5 * 60 * 1000, // 5 minutes cache
            onError: (err) => {
                console.warn(`VickyAI ${aiModel} fetch failed:`, err)
            }
        }
    )

    /* Vicky's API can return several shapes depending on the endpoint:
     *  - `[product, ...]`              — most endpoints
     *  - `{data: [...]}`               — useCustomQuery wrapper
     *  - `{top_result: [...ids], ...}` — semantic-search (bare ID strings)
     * For semantic-search, convert each id string to a minimal `{id}` object
     * so `useEnrichVickyProducts` can fetch the full master from SFCC. */
    const dataArray = useMemo(() => {
        if (Array.isArray(recommendations)) return recommendations
        if (Array.isArray(recommendations?.data)) return recommendations.data
        if (Array.isArray(recommendations?.top_result))
            return recommendations.top_result.map((id) => ({id}))
        return []
    }, [recommendations])

    const productIds = useMemo(() => dataArray.map((item) => item.id).join(','), [dataArray])

    /* Reshape Vicky's variationGroup-shaped items into PLP-style master
     * products (with `representedProduct` pinning the recommended color).
     * The master fetch now also pulls prices / promotions / availability
     * so the tile's `getPriceData` works without a second SFCC call. The
     * shared hook is also used by notify-me, shop-similar and recently-
     * viewed so all surfaces get identical tile behaviour. */
    const enrichedProducts = useEnrichVickyProducts(dataArray, {
        enabled: !isLoading
    })

    return {
        products: enrichedProducts,
        // Only show loading on initial fetch, not during enrichment
        isLoading,
        productIds
    }
}
